import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useUserData } from '@/libs/fleet-api/hooks';
import {
	PrintVerificationSearchMPPParam,
	usePrintVerificationSearchMPP,
	usePrintVerificationSearchOptionQuery,
} from '@/libs/fleet-api/services/print-verification';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Form, Input, Row, Select, Table } from 'antd';
import { FC, useState } from 'react';

const defaultSeachParams: PrintVerificationSearchMPPParam = {
	pagination: {
		pageNumber: 1,
		pageSize: 10,
	},
};

const VerifyDisbursementListPage: FC = () => {
	const { navigate } = useApp();
	const { groupName } = useUserData() ?? {};
	const { openCustomerDetail, openCustomerGroupDetail } = useModals();

	const { form, fieldName, value } = useForm<{
		searchType: string;
		searchParam: PrintVerificationSearchMPPParam;
	}>();

	const searchOptionQuery = usePrintVerificationSearchOptionQuery();
	const {
		searchByOptions = [],
		divisionOptions = [],
		fbdhOptions = [],
		frmOptions = [],
	} = searchOptionQuery.data?.data ?? {};

	const [searchParam, setSearchParam] =
		useState<PrintVerificationSearchMPPParam>(defaultSeachParams);

	const { data, isFetching } = usePrintVerificationSearchMPP(searchParam);
	const dataSource = data?.data.items ?? [];

	const renderSearchType = () => {
		switch (value?.searchType) {
			case 'mpp-no':
			case 'customer-name':
			case 'group-bmpp':
				return (
					<Form.Item name={fieldName(['searchParam', 'search', 'value'])}>
						<Input />
					</Form.Item>
				);
			case 'division':
				return (
					<Form.Item name={fieldName(['searchParam', 'divisionId'])}>
						<Select
							options={divisionOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);
			case 'frm-id':
				return (
					<Form.Item name={fieldName(['searchParam', 'frmId'])}>
						<Select
							options={frmOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);
			case 'fbdh-id':
				return (
					<Form.Item name={fieldName(['searchParam', 'fbdhId'])}>
						<Select
							options={fbdhOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);
			default:
				return (
					<Form.Item>
						<Input disabled />
					</Form.Item>
				);
		}
	};

	return (
		<div>
			<FleetTitle style={{ marginBottom: 20 }}>
				{groupName === 'FCOH'
					? 'Verifikasi Golive Confirmation'
					: 'Verifikasi dan Upload Dokumen Kesepakatan Realisasi Pencairan Pembiayaan'}
			</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={(values) => {
					if (values.searchType) {
						if (
							['mpp-no', 'customer-name', 'group-bmpp'].includes(
								values.searchType,
							)
						) {
							values.searchParam.search!.by = values.searchType;
						}

						setSearchParam({
							...values.searchParam,
							pagination: {
								pageNumber: 1,
								pageSize: 10,
							},
						});
					} else {
						setSearchParam(defaultSeachParams);
					}
				}}
			>
				<Row gutter={[20, 20]}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchType')}
							label={<span>Kata Kunci</span>}
							labelCol={{ flex: 'auto', style: { width: 150 } }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								loading={searchOptionQuery.isLoading}
								disabled={isFetching}
								placeholder="Search By"
								options={searchByOptions.map((option) => {
									return {
										label: option.value,
										value: option.id,
									};
								})}
								allowClear
								style={{
									display: 'block',
									width: '100%',
								}}
								onSelect={() => {
									form.resetFields([fieldName('searchParam')]);
								}}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">{renderSearchType()}</Col>
					<Col>
						<Form.Item>
							<Button
								block
								loading={isFetching}
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>
			<Divider />
			<Table
				bordered
				scroll={{ x: 'max-content' }}
				loading={isFetching}
				dataSource={dataSource.map((data, key) => {
					return { ...data, key: data.mppNo ?? key };
				})}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						width: 1,
						render: (_, __, index) =>
							(searchParam.pagination.pageNumber - 1) *
								searchParam.pagination.pageSize +
							index +
							1,
					},
					{
						title: 'No MPP',
						render: (_, record) =>
							record.mppId && record.mppNo ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('MPPPage', {
											mppId: record.mppId,
											viewOnly: true,
											onBack: () => {
												navigate('VerifyDisbursementListPage');
											},
										});
									}}
								>
									{record.mppNo}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'Nama Customer',
						render: (_, record) =>
							record.customerId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerDetail(String(record.customerId));
									}}
								>
									{record.customerName}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'Group BMPP',
						render: (_, record) =>
							record.bmppGroupId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(record.bmppGroupId);
									}}
								>
									{record.bmppGroupName}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'FRM',
						render: (_, record) => record.frm ?? '-',
					},
					{
						title: 'FBDH',
						render: (_, record) => record.fbdh ?? '-',
					},
					{
						title: 'Divisi',
						render: (_, record) => record.division ?? '-',
					},
					{
						title: 'Action',
						align: 'center',
						fixed: 'right',
						render: (_, record) => {
							return (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										navigate('VerifyDisbursementPOListPage', {
											mppId: record.mppId,
										});
									}}
								>
									<ArrowRightOutlined />
								</a>
							);
						},
					},
				]}
				pagination={{
					total: data?.data.totalCount,
					current: searchParam.pagination.pageNumber,
					pageSize: searchParam.pagination.pageSize,
					onChange: (pageNumber, pageSize) => {
						setSearchParam((current) => {
							return {
								...current,
								pagination: {
									pageSize,
									pageNumber:
										current.pagination.pageSize !== pageSize ? 1 : pageNumber,
								},
							};
						});
					},
				}}
			/>
		</div>
	);
};

export default VerifyDisbursementListPage;
